import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Sun,
  Moon,
  LogOut,
  Settings as SettingsIcon,
  ChevronDown,
  Menu,
  LayoutDashboard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useTheme } from "../../theme/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import useHalalFeature from "../../hooks/useHalalFeature";
import {
  BRAND,
  EDITORIAL_FONT,
} from "../editorial/EditorialPrimitives";

const APP_STORE_URL = "https://apps.apple.com/us/app/muslim-prayer-companion/id6745054019";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.psu.pdxmsa&pcampaignid=web_share";

function openAppStore() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const url = isIOS ? APP_STORE_URL : isAndroid ? PLAY_STORE_URL : APP_STORE_URL;
  window.open(url, "_blank", "noopener,noreferrer");
}

const NAV_LINKS = [
  { id: "home", label: "Home", path: "/" },
  { id: "masjids", label: "Masajid", path: "/masjid-info" },
  { id: "halal", label: "Halal", path: "/halal-info" },
  { id: "solutions", label: "Solutions", path: "/solutions" },
];

export default function SiteNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { appearance, toggleTheme } = useTheme();
  const { user, userProfile, loading: authLoading, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { halalEnabled } = useHalalFeature();

  // Drop the Halal link entirely while the halal feature is turned off
  const navLinks = halalEnabled
    ? NAV_LINKS
    : NAV_LINKS.filter((link) => link.id !== "halal");

  const isDark = appearance === "dark";

  // Palette adapts so the seam between SiteNav and editorial pages disappears
  // on light theme, and remains coherent on dark admin pages.
  const palette = isDark
    ? {
        bg: BRAND.ink,
        bgScrim: "rgba(26,23,19,0.85)",
        text: BRAND.ivory,
        muted: "#9a9288",
        border: "#2a2520",
        chip: "#ffffff10",
      }
    : {
        bg: BRAND.ivory,
        bgScrim: "rgba(250,247,241,0.88)",
        text: BRAND.ink,
        muted: BRAND.muted,
        border: BRAND.hairline,
        chip: "#00000008",
      };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleNavigate = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  const needsSignup = user && userProfile?.signupComplete === false;

  const isPendingMasjidAdmin =
    user &&
    userProfile?.role === "masjid" &&
    (userProfile?.pendingMasjidApproval === true ||
      !userProfile?.isAdmin ||
      (!userProfile?.masjidId && !userProfile?.activeMasjidId));

  const isIncompleteHalalUser =
    user &&
    userProfile?.role === "halal" &&
    userProfile?.restaurantProfileComplete === false;

  const hasDashboardAccess =
    !isPendingMasjidAdmin &&
    !needsSignup &&
    !isIncompleteHalalUser &&
    (userProfile?.role === "super" ||
      userProfile?.role === "halal" ||
      userProfile?.isAdmin);

  const getDashboardRoute = () => {
    if (!user) return "/login";
    if (needsSignup) return "/signup";
    if (isPendingMasjidAdmin) return "/masjid-signup";
    if (isIncompleteHalalUser) return "/halal-signup";
    if (userProfile?.role === "super") return "/dashboard";
    if (userProfile?.role === "halal") return "/halal";
    if (userProfile?.isAdmin) return "/masjid-dashboard";
    return null;
  };

  const isActive = (path) => location.pathname === path;

  /* ── Brand wordmark ── */
  const Wordmark = ({ size = 22 }) => (
    <span
      style={{
        fontSize: size,
        fontWeight: 500,
        lineHeight: 1,
        letterSpacing: "-0.015em",
        fontFamily: EDITORIAL_FONT.sans,
        color: palette.text,
      }}
    >
      Muslim+
    </span>
  );

  return (
    <nav
      className="sticky top-0 z-50 backdrop-blur-xl"
      style={{
        background: palette.bgScrim,
        borderBottom: `1px solid ${palette.border}`,
        fontFamily: EDITORIAL_FONT.sans,
      }}
    >
      <div className="container mx-auto flex h-16 items-center justify-between gap-4 px-4 sm:h-[72px] sm:px-6 lg:px-10">
        {/* Brand */}
        <button
          type="button"
          onClick={() => navigate("/")}
          className="flex items-center transition-opacity hover:opacity-80"
          aria-label="Muslim+ home"
        >
          <Wordmark size={24} />
        </button>

        {/* Desktop nav */}
        <div className="hidden items-center gap-1 sm:flex">
          {navLinks.map((link) => {
            const active = isActive(link.path);
            return (
              <button
                key={link.id}
                onClick={() => navigate(link.path)}
                className="relative rounded-full px-4 py-2 text-sm transition-colors"
                style={{
                  color: active ? BRAND.gold : palette.text,
                  fontWeight: active ? 600 : 450,
                  background: active ? palette.chip : "transparent",
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.color = BRAND.gold;
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.color = palette.text;
                }}
              >
                {link.label}
              </button>
            );
          })}
        </div>

        {/* Right cluster */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="hidden h-9 w-9 items-center justify-center rounded-full transition-colors sm:inline-flex"
            style={{ color: palette.muted }}
            onMouseEnter={(e) => (e.currentTarget.style.color = BRAND.gold)}
            onMouseLeave={(e) => (e.currentTarget.style.color = palette.muted)}
            aria-label="Toggle theme"
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          {!authLoading && user && userProfile ? (
            <>
              {/* Hairline divider */}
              <div
                className="hidden h-5 w-px sm:block"
                style={{ background: palette.border }}
              />

              {needsSignup && (
                <button
                  onClick={() =>
                    navigate("/signup", {
                      state: { startAtStep: 2, email: user?.email || "" },
                    })
                  }
                  className="hidden h-9 items-center gap-1.5 rounded-full px-4 text-sm font-medium transition-opacity hover:opacity-90 sm:inline-flex"
                  style={{ background: BRAND.ink, color: BRAND.ivory }}
                >
                  Complete signup
                  <span aria-hidden>→</span>
                </button>
              )}
              {isPendingMasjidAdmin && !needsSignup && (
                <button
                  onClick={() => navigate("/masjid-signup")}
                  className="hidden h-9 items-center gap-1.5 rounded-full px-4 text-sm font-medium transition-opacity hover:opacity-90 sm:inline-flex"
                  style={{ background: BRAND.ink, color: BRAND.ivory }}
                >
                  Complete signup
                  <span aria-hidden>→</span>
                </button>
              )}
              {isIncompleteHalalUser && !needsSignup && !isPendingMasjidAdmin && (
                <button
                  onClick={() => navigate("/halal-signup")}
                  className="hidden h-9 items-center gap-1.5 rounded-full px-4 text-sm font-medium transition-opacity hover:opacity-90 sm:inline-flex"
                  style={{ background: BRAND.ink, color: BRAND.ivory }}
                >
                  Complete signup
                  <span aria-hidden>→</span>
                </button>
              )}
              {hasDashboardAccess &&
                !needsSignup &&
                !isPendingMasjidAdmin &&
                !isIncompleteHalalUser && (
                  <button
                    onClick={() => {
                      const dashboardRoute = getDashboardRoute();
                      if (dashboardRoute) navigate(dashboardRoute);
                    }}
                    className="hidden h-9 items-center gap-1.5 rounded-full px-4 text-sm font-medium transition-opacity hover:opacity-90 sm:inline-flex"
                    style={{ background: BRAND.ink, color: BRAND.ivory }}
                  >
                    Dashboard
                  </button>
                )}

              {!needsSignup && !isPendingMasjidAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-9 gap-1.5 rounded-full px-2 transition-colors hover:bg-transparent"
                    >
                      <Avatar
                        className="h-7 w-7"
                        style={{
                          border: `1px solid ${palette.border}`,
                        }}
                      >
                        <AvatarFallback
                          className="text-xs font-semibold"
                          style={{
                            background: BRAND.gold,
                            color: "#fff",
                            fontFamily: EDITORIAL_FONT.serif,
                            fontSize: 13,
                          }}
                        >
                          {user?.email?.charAt(0).toUpperCase() || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <span
                        className="hidden max-w-[120px] truncate text-sm md:inline"
                        style={{ color: palette.text, fontWeight: 500 }}
                      >
                        {user?.email?.split("@")[0]}
                      </span>
                      <ChevronDown className="h-3.5 w-3.5" style={{ color: palette.muted }} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-60 rounded-xl p-1.5"
                    style={{
                      background: palette.bg,
                      border: `1px solid ${palette.border}`,
                    }}
                  >
                    <DropdownMenuLabel className="px-3 py-2.5">
                      <div className="flex flex-col gap-1">
                        <p
                          className="truncate text-sm leading-none"
                          style={{
                            fontFamily: EDITORIAL_FONT.serif,
                            fontSize: 16,
                            fontWeight: 500,
                            color: palette.text,
                          }}
                        >
                          {user?.email}
                        </p>
                        <p
                          className="text-[10px] uppercase leading-none"
                          style={{ letterSpacing: "0.14em", color: palette.muted }}
                        >
                          {userProfile?.isAdmin ? userProfile?.role || "Admin" : "Member"}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator style={{ background: palette.border }} />
                    <DropdownMenuItem
                      onClick={() => navigate("/settings")}
                      className="cursor-pointer rounded-lg px-3 py-2 text-sm"
                      style={{ color: palette.text }}
                    >
                      <SettingsIcon className="mr-2 h-4 w-4" style={{ color: palette.muted }} />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="cursor-pointer rounded-lg px-3 py-2 text-sm"
                      style={{ color: BRAND.terracotta }}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          ) : !authLoading ? (
            <>
              <button
                onClick={() => navigate("/login")}
                className="hidden h-9 items-center rounded-full px-3 text-sm transition-colors sm:inline-flex"
                style={{ color: palette.text, fontWeight: 450 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = BRAND.gold)}
                onMouseLeave={(e) => (e.currentTarget.style.color = palette.text)}
              >
                Sign in
              </button>
              <button
                onClick={openAppStore}
                className="hidden h-9 items-center gap-1.5 rounded-full px-4 text-sm font-medium transition-opacity hover:opacity-90 sm:inline-flex"
                style={{ background: BRAND.ink, color: BRAND.ivory }}
              >
                Get the app
              </button>
            </>
          ) : null}

          {/* Mobile menu trigger */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <button
                className="inline-flex h-9 w-9 items-center justify-center rounded-full sm:hidden"
                style={{ color: palette.text }}
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[300px] p-0 sm:w-[340px]"
              style={{
                background: palette.bg,
                borderColor: palette.border,
                color: palette.text,
              }}
            >
              <SheetHeader className="px-6 pt-6">
                <SheetTitle asChild>
                  <div className="flex items-center justify-start">
                    <Wordmark size={22} />
                  </div>
                </SheetTitle>
              </SheetHeader>

              <div
                className="my-5 mx-6 h-px"
                style={{ background: palette.border }}
              />

              <div className="flex flex-col gap-1 px-4">
                <div
                  className="px-3 pb-2 text-[10px] font-semibold uppercase"
                  style={{ letterSpacing: "0.16em", color: palette.muted }}
                >
                  Browse
                </div>
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => handleNavigate(link.path)}
                    className="rounded-lg px-3 py-2.5 text-left text-base transition-colors"
                    style={{
                      color: isActive(link.path) ? BRAND.gold : palette.text,
                      fontWeight: isActive(link.path) ? 600 : 450,
                      fontFamily: EDITORIAL_FONT.serif,
                      fontSize: 18,
                    }}
                  >
                    {link.label}
                  </button>
                ))}
              </div>

              {!authLoading && user && userProfile && (
                <>
                  <div
                    className="my-4 mx-6 h-px"
                    style={{ background: palette.border }}
                  />
                  <div className="flex flex-col gap-1 px-4">
                    <div
                      className="px-3 pb-2 text-[10px] font-semibold uppercase"
                      style={{ letterSpacing: "0.16em", color: palette.muted }}
                    >
                      Account
                    </div>

                    {isIncompleteHalalUser && (
                      <button
                        onClick={() => handleNavigate("/halal-signup")}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium"
                        style={{ background: BRAND.gold, color: "#fff" }}
                      >
                        <LayoutDashboard className="h-4 w-4" />
                        Complete signup
                      </button>
                    )}
                    {hasDashboardAccess && (
                      <button
                        onClick={() => {
                          const dashboardRoute = getDashboardRoute();
                          if (dashboardRoute) handleNavigate(dashboardRoute);
                        }}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium"
                        style={{ background: BRAND.ink, color: BRAND.ivory }}
                      >
                        <LayoutDashboard className="h-4 w-4" />
                        Dashboard
                      </button>
                    )}
                    <button
                      onClick={() => handleNavigate("/settings")}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors"
                      style={{ color: palette.text }}
                    >
                      <SettingsIcon className="h-4 w-4" style={{ color: palette.muted }} />
                      Settings
                    </button>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm"
                      style={{ color: BRAND.terracotta }}
                    >
                      <LogOut className="h-4 w-4" />
                      Log out
                    </button>
                  </div>
                </>
              )}

              {!authLoading && !user && (
                <>
                  <div
                    className="my-4 mx-6 h-px"
                    style={{ background: palette.border }}
                  />
                  <div className="flex flex-col gap-2 px-4">
                    <button
                      onClick={() => handleNavigate("/login")}
                      className="rounded-full border px-4 py-2.5 text-center text-sm font-medium transition-colors"
                      style={{
                        borderColor: palette.border,
                        color: palette.text,
                        background: "transparent",
                      }}
                    >
                      Sign in
                    </button>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        openAppStore();
                      }}
                      className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-medium transition-opacity hover:opacity-90"
                      style={{ background: BRAND.ink, color: BRAND.ivory }}
                    >
                      Get the app
                    </button>
                  </div>
                </>
              )}

              {/* Theme toggle */}
              <div
                className="my-4 mx-6 h-px"
                style={{ background: palette.border }}
              />
              <div className="px-4 pb-6">
                <button
                  onClick={toggleTheme}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors"
                  style={{ color: palette.text }}
                >
                  {isDark ? (
                    <Sun className="h-4 w-4" style={{ color: BRAND.gold }} />
                  ) : (
                    <Moon className="h-4 w-4" style={{ color: BRAND.muted }} />
                  )}
                  {isDark ? "Light mode" : "Dark mode"}
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
